import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { VueConvention } from './useConventions';
import { appelApi } from './api';
import { TOUS_LES_SITES } from '../site/context';
import type { FicheEntretien, OrientationEntretien, ReferentielFiche } from './useEntretiens';
import type { ExemplaireSigne, SuiviDocument } from './useDocuments';

// ---------------------------------------------------------------------------
// Types renvoyés par l'API du pôle administratif
// ---------------------------------------------------------------------------

export type StatutPiece = 'ATTENDUE' | 'DEPOSEE' | 'VERIFIEE' | 'VALIDEE' | 'REFUSEE';
export type ModeDossier = 'CFA' | 'OF';
/** Moyen de déplacement déclaré par le candidat, pesé au matching. */
export type Mobilite = 'PERMIS_B' | 'TRANSPORTS_EN_COMMUN' | 'AUCUNE';

export interface LigneApprenant {
  id: string;
  nom: string;
  prenom: string;
  initiales: string;
  email: string | null;
  age: number | null;
  formation: string | null;
  formationCode: string | null;
  siteCode: string;
  statut: string;
  statutLibelle: string;
  statutTon: string;
  mode: ModeDossier;
  mineur: boolean;
  rqth: boolean;
  pieces: { deposees: number; attendues: number };
  /** OPCO de l'entreprise du contrat en cours : le financeur du parcours. */
  opco: string | null;
}

export interface CompteursInscriptions {
  inscriptionsDuMois: number;
  entretiensAMener: number;
  liensAccesEnvoyes: number;
  dossiersIncomplets: number;
}

/**
 * Effectif par statut, dans l'ordre du parcours, tel que l'API le renvoie.
 * C'est la source du filtre de statut : les libellés viennent du référentiel
 * serveur, le front ne les recopie pas.
 */
export interface RepartitionStatut {
  statut: string;
  libelle: string;
  ton: string;
  dossiers: number;
}

export interface CvCandidat {
  pieceId: string;
  fichiers: FichierPieceDossier[];
}

export interface LigneCandidatPlacement {
  id: string;
  nom: string;
  prenom: string;
  initiales: string;
  formation: string | null;
  formationCode: string | null;
  statut: string;
  statutLibelle: string;
  statutTon: string;
  cv: CvCandidat | null;
}

interface EnteteListeApprenants {
  compteurs: CompteursInscriptions;
  statuts: RepartitionStatut[];
  total: number;
  page: number;
  taille: number;
}

export type ReponseListeApprenants = EnteteListeApprenants &
  (
    | { vue: 'COMPLETE'; apprenants: LigneApprenant[] }
    | { vue: 'PLACEMENT'; apprenants: LigneCandidatPlacement[] }
  );

export function lignesCompletes(reponse: ReponseListeApprenants | undefined): LigneApprenant[] {
  return reponse?.vue === 'COMPLETE' ? reponse.apprenants : [];
}

export function urlCv(pieceId: string, fichierId: string): string {
  return `/api/apprenants/cv/${pieceId}/fichiers/${fichierId}`;
}

export interface FichierPieceDossier {
  id: string;
  nomFichier: string;
  tailleOctets: number;
  typeMime: string;
  deposeLe: string;
}

export interface PieceDossier {
  id: string;
  type: string;
  libelle: string;
  obligatoire: boolean;
  statut: StatutPiece;
  /**
   * Ses fichiers se téléchargent, donc s'impriment : administratif et
   * direction, le CV aussi pour les commerciaux (TF-490). Sinon, consultation
   * à l'écran seulement.
   */
  telechargeable: boolean;
  /** Fichiers à fournir pour que la pièce compte comme déposée. */
  nombreAttendu: number;
  /** Fichiers acceptés au maximum. */
  nombreMaximum: number;
  /** Types MIME acceptés, tels que l'API les impose. */
  formatsAcceptes: string[];
  /** « PDF, JPEG, PNG ou HEIC », prêt à afficher. */
  libelleFormats: string;
  tailleMaxOctets: number;
  fichiers: FichierPieceDossier[];
  deposeeLe: string | null;
  verifieeLe: string | null;
  motifRefus: string | null;
}

/** Rubriques du CERFA portées par le dossier, telles que l'API les renvoie. */
export interface BlocCerfa {
  nomUsage: string | null;
  nir: string | null;
  departementNaissance: string | null;
  regimeSocial: string | null;
  sportifHautNiveau: boolean;
  rqthEquivalenceJeunes: boolean;
  rqthExtensionBoe: boolean;
  projetCreationEntreprise: boolean;
  numeroVoie: string | null;
  voie: string | null;
  complementAdresse: string | null;
  situationAvantContrat: string | null;
  dernierDiplomePrepare: string | null;
  derniereClasseSuivie: string | null;
  intitulePrecisDernierDiplome: string | null;
  diplomeLePlusEleve: string | null;
  representant: {
    nom: string | null;
    prenom: string | null;
    numeroVoie: string | null;
    voie: string | null;
    complement: string | null;
    codePostal: string | null;
    commune: string | null;
    courriel: string | null;
  };
}

/**
 * Document produit par la plateforme, à distinguer d'une pièce justificative :
 * une pièce est déposée par l'apprenant et se valide, un document est généré et
 * se consulte.
 */
export interface DocumentDossier {
  id: string;
  type: string;
  libelle: string;
  statut: string;
  modele: string | null;
  genereLe: string;
  suivi: SuiviDocument;
  /** Exemplaire signé déposé en retour ; `null` tant qu'il n'y en a pas. */
  signe: ExemplaireSigne | null;
  /** Faux pour un document qui ne se signe pas (calendrier de formation). */
  signable?: boolean;
  /** Circuit de validation par la direction, propre à la convention de formation (TF-430). */
  convention?: VueConvention | null;
}

export interface Dossier {
  identite: {
    id: string;
    nom: string;
    prenom: string;
    dateNaissance: string | null;
    age: number | null;
    lieuNaissance: string | null;
    sexe: 'M' | 'F' | null;
    nationalite: string | null;
    ine: string | null;
    email: string | null;
    telephone: string | null;
    adresse: string | null;
    codePostal: string | null;
    ville: string | null;
  };
  /**
   * Bloc « L'apprenti(e) » du CERFA. `null` en mode OF : ce qui est propre à
   * l'apprentissage est masqué, pas affiché vide — l'API ne l'envoie même pas.
   */
  cerfa: BlocCerfa | null;
  situation: {
    mineur: boolean;
    rqth: boolean;
    hebergeParTiers: boolean;
    dejaApprenti: boolean;
    demandeurEmploi: boolean;
    titulaireDiplome: boolean;
  };
  dossier: {
    mode: ModeDossier;
    statut: string;
    statutLibelle: string;
    statutTon: string;
    site: { code: string; nom: string };
    formation: { code: string; titre: string; niveau: string; rncp: string } | null;
    session: { id: string; libelle: string; dateDebut: string; dateFin: string } | null;
    conseiller: { id: string; nom: string; prenom: string } | null;
    compte: { email: string; dernierAcces: string | null } | null;
    dateInscription: string | null;
    dateEntree: string | null;
    dateFinPrevue: string | null;
    dateExamen: string | null;
    carteEtudianteFaite: boolean;
    carteEtudianteLe: string | null;
    /** Critère du barème de matching des offres (CDC §6.9). */
    mobilite: Mobilite | null;
    /** Candidat en recherche d'entreprise : pilote le matching commercial. */
    rechercheEntreprise: boolean;
    /** Début de la recherche, posé avec l'indicateur ci-dessus. */
    rechercheDepuisLe: string | null;
    lienAccesEnvoyeLe: string | null;
  };
  completude: {
    attendues: number;
    deposees: number;
    validees: number;
    pourcentage: number;
    complet: boolean;
  };
  jalons: { libelle: string; atteint: boolean }[];
  /**
   * Ce que le lecteur peut faire des fichiers déposés (TF-464, TF-490), décidé
   * par l'API : facturation, recrutement et pédagogique les consultent sans
   * les télécharger, le formateur ne les ouvre pas. Le téléchargement se lit
   * pièce par pièce (`PieceDossier.telechargeable`).
   */
  accesPieces: { consulter: boolean; telecharger: boolean };
  pieces: PieceDossier[];
  documents: DocumentDossier[];
  /**
   * Périodes d'immersion du dossier et leur compte rendu d'entretien. Le
   * document est le même que celui du dossier de l'entreprise : il est désigné
   * par l'immersion, pas dupliqué.
   */
  immersions: ImmersionDuDossier[];
  entreprise: {
    /** Identifiant de la fiche : c'est lui qui mène au dossier de l'organisation. */
    id: string;
    denomination: string;
    siret: string;
    ville: string | null;
    opco: string | null;
    idcc: string | null;
  } | null;
  tuteur: {
    nom: string;
    prenom: string;
    emploiOccupe: string | null;
    anneesExperience: number | null;
    email: string | null;
    telephone: string | null;
  } | null;
  contrat: {
    statut: string;
    modeleCerfa: string | null;
    dateDebut: string;
    dateFin: string;
    finPeriodeEssai: string | null;
    echeance5Mois: string | null;
    rupture: { dateRupture: string | null; formationMaintenue: boolean } | null;
    /**
     * Fin du maintien en formation après une rupture, calculée par l'API.
     * `dateRuptureConnue` faux : comptée depuis la déclaration de la rupture.
     */
    finMaintien: { fin: string; dateRuptureConnue: boolean } | null;
  } | null;
  remuneration: {
    tranche: string;
    bareme: string;
    colonnes: string[];
    valeurs: { pourcentage: string; montant: string }[];
    note: string;
  } | null;
  alertes: {
    id: string;
    code: string;
    libelle: string;
    declencheur: string;
    message: string;
  }[];
  relances: RelancesDossier;
  /**
   * Dernier entretien de recrutement et sa fiche, servis avec le dossier : ce
   * qui a été recueilli pendant l'entretien se relit ici, sans repasser par le
   * tableau du pôle commercial. `null` pour les profils qui ne tiennent pas le
   * dossier — la fiche porte des données de santé et un suivi judiciaire.
   */
  entretien: {
    id: string;
    dateEntretien: string;
    resultat: string;
    compteRendu: string | null;
    besoinsRecueillis: string | null;
    fiche: FicheEntretien | null;
    /** Orientation d'un candidat non retenu ; `null` pour toute autre issue. */
    orientation: OrientationEntretien | null;
  } | null;
  /** Libellés des cases de la fiche : l'écran du dossier ne les connaît pas. */
  referentielFiche: ReferentielFiche;
}

/** Canal d'un envoi, tel que l'API le nomme. */
export type CanalEnvoi = 'EMAIL' | 'SMS' | 'IN_APP';

/** `ENVOYE` : parti chez le prestataire. `JOURNALISE` : tracé sans expédition. */
export type IssueEnvoi = 'ENVOYE' | 'JOURNALISE' | 'ECHEC';

export interface EnvoiRelanceDossier {
  id: string;
  scenario: string | null;
  canal: CanalEnvoi;
  /** Au candidat, ou au responsable du dossier dans un récapitulatif. */
  pour: 'CIBLE' | 'RESPONSABLE';
  destinataire: string;
  issue: IssueEnvoi;
  message: string;
  /** Vrai quand l'envoi vient du passage automatique, faux pour un envoi manuel. */
  automatique: boolean;
  creeLe: string;
}

/**
 * Raison pour laquelle aucune relance n'est attendue. `null` quand il y en a
 * bien une de prévue.
 */
export type MotifSuspensionRelance =
  | 'DOSSIER_COMPLET'
  | 'SCENARIO_INACTIF'
  | 'LIEN_NON_ENVOYE'
  | 'STATUT_HORS_RELANCE'
  | 'SANS_COORDONNEES'
  | 'SANS_DESTINATAIRE'
  | 'CADENCE_TERMINEE';

/** Cadence, prochaine échéance et historique des relances d'un dossier. */
export interface RelancesDossier {
  cadence: string;
  scenarioActif: boolean;
  /** Qui reçoit la relance des pièces : le candidat, son responsable, ou les deux. */
  destinataires: 'CIBLE' | 'RESPONSABLE' | 'LES_DEUX';
  libelleDestinataires: string;
  /** Responsable en charge qui reçoit la relance, s'il y en a un. */
  responsable: { prenom: string; nom: string } | null;
  /** Aucun responsable en charge : la relance part au pôle administratif de l'antenne. */
  sansResponsable: boolean;
  canaux: CanalEnvoi[];
  /** Canaux réellement expédiés ; les autres sont seulement tracés. */
  canauxExpedies: CanalEnvoi[];
  piecesADeposer: number;
  lienEnvoyeLe: string | null;
  prochaineLe: string | null;
  motifSuspension: MotifSuspensionRelance | null;
  envois: EnvoiRelanceDossier[];
}

// ---------------------------------------------------------------------------
// Requêtes
// ---------------------------------------------------------------------------

export interface FiltresApprenants {
  site: string;
  statut?: string;
  mode?: ModeDossier;
  /** Code du titre professionnel, ex. `CDV`. */
  formation?: string;
  mineur?: boolean;
  rqth?: boolean;
  recherche?: string;
  page?: number;
  taille?: number;
}

function versParametres(filtres: FiltresApprenants): string {
  const parametres = new URLSearchParams();
  if (filtres.site && filtres.site !== TOUS_LES_SITES) parametres.set('site', filtres.site);
  if (filtres.statut) parametres.set('statut', filtres.statut);
  if (filtres.mode) parametres.set('mode', filtres.mode);
  if (filtres.formation) parametres.set('formation', filtres.formation);
  if (filtres.mineur) parametres.set('mineur', 'true');
  if (filtres.rqth) parametres.set('rqth', 'true');
  if (filtres.recherche) parametres.set('recherche', filtres.recherche);
  if (filtres.page && filtres.page > 1) parametres.set('page', String(filtres.page));
  if (filtres.taille) parametres.set('taille', String(filtres.taille));
  const chaine = parametres.toString();
  return chaine ? `?${chaine}` : '';
}

export function useApprenants(filtres: FiltresApprenants) {
  return useQuery({
    queryKey: ['apprenants', filtres],
    queryFn: () => appelApi<ReponseListeApprenants>(`/apprenants${versParametres(filtres)}`),
  });
}

export interface FichePlacement {
  id: string;
  nom: string;
  prenom: string;
  formation: { code: string; titre: string } | null;
  mode: ModeDossier;
  statut: string;
  statutLibelle: string;
  statutTon: string;
  rechercheEntreprise: boolean;
  rechercheDepuisLe: string | null;
  cv: CvCandidat | null;
  entreprise: { id: string; denomination: string } | null;
  immersions: ImmersionDuDossier[];
}

export type ReponseDossier =
  | { vue: 'COMPLETE'; dossier: Dossier }
  | { vue: 'PLACEMENT'; fiche: FichePlacement };

export function useDossier(id: string | undefined) {
  return useQuery({
    queryKey: ['dossier', id],
    queryFn: () => appelApi<ReponseDossier>(`/apprenants/${id}`),
    enabled: Boolean(id),
  });
}

export function dossierComplet(reponse: ReponseDossier | undefined): Dossier | undefined {
  return reponse?.vue === 'COMPLETE' ? reponse.dossier : undefined;
}

/** Personne assignable comme conseiller référent d'un dossier. */
export interface ConseillerAssignable {
  id: string;
  nom: string;
  prenom: string;
  profil: string;
}

/**
 * Conseillers assignables, bornés par l'API au périmètre de sites : la liste
 * proposée est exactement celle que la mise à jour du dossier accepte.
 */
export function useConseillers(actif: boolean) {
  return useQuery({
    queryKey: ['conseillers'],
    queryFn: () => appelApi<{ conseillers: ConseillerAssignable[] }>('/apprenants/conseillers'),
    enabled: actif,
  });
}

/** Nouvelle inscription saisie par l'administratif (CDC §4.1). */
export interface EntreeInscription {
  nom: string;
  prenom: string;
  siteCode: string;
  dateNaissance?: string;
  lieuNaissance?: string;
  sexe?: 'M' | 'F';
  nationalite?: string;
  email?: string;
  telephone?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  formationCode?: string;
  mode: ModeDossier;
  /** Situation : elle détermine les pièces conditionnelles attendues. */
  hebergeParTiers: boolean;
  rqth: boolean;
  dejaApprenti: boolean;
  demandeurEmploi: boolean;
  titulaireDiplome: boolean;
  dateEntree?: string;

  // Bloc « L'apprenti(e) » du CERFA FA13. Tout y est facultatif : une
  // inscription se prend souvent au téléphone, et le dossier se complète après.
  nomUsage?: string;
  nir?: string;
  departementNaissance?: string;
  /** Code du référentiel `cerfaApprenti.regimesSociaux`. */
  regimeSocial?: string;
  sportifHautNiveau: boolean;
  rqthEquivalenceJeunes: boolean;
  rqthExtensionBoe: boolean;
  projetCreationEntreprise: boolean;
  /** Adresse éclatée comme sur le formulaire ; l'API recompose la ligne. */
  numeroVoie?: string;
  voie?: string;
  complementAdresse?: string;
  /** Codes des référentiels `cerfaApprenti.*`, jamais des libellés. */
  situationAvantContrat?: string;
  dernierDiplomePrepare?: string;
  derniereClasseSuivie?: string;
  intitulePrecisDernierDiplome?: string;
  diplomeLePlusEleve?: string;
  representantNom?: string;
  representantPrenom?: string;
  representantNumeroVoie?: string;
  representantVoie?: string;
  representantComplement?: string;
  representantCodePostal?: string;
  representantCommune?: string;
  representantCourriel?: string;
}

export interface ApprenantCree {
  id: string;
}

export function useCreerApprenant() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: EntreeInscription) =>
      appelApi<ApprenantCree>('/apprenants', { methode: 'POST', corps: entree }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['apprenants'] });
    },
  });
}

/**
 * Résultat de l'entretien de recrutement (CDC §4.1).
 *
 * `besoinsRecueillis` est le compte rendu de la fiche d'entretien : il part
 * avec l'issue, dans le même enregistrement, parce que c'est lui qui sert
 * ensuite au matching entreprise. Le lien d'accès n'est envoyé que sur un
 * candidat retenu — l'API le décide, pas l'écran.
 */
export function useEnregistrerEntretien() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      id: string;
      resultat: 'RETENU' | 'NON_RETENU' | 'A_REVOIR';
      compteRendu?: string;
      besoinsRecueillis?: string;
    }) =>
      appelApi<{ lienEnvoye: boolean }>(`/apprenants/${variables.id}/entretien`, {
        methode: 'POST',
        corps: {
          resultat: variables.resultat,
          compteRendu: variables.compteRendu,
          besoinsRecueillis: variables.besoinsRecueillis,
          envoyerLienAcces: true,
        },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['apprenants'] });
      void client.invalidateQueries({ queryKey: ['dossier'] });
      void client.invalidateQueries({ queryKey: ['positionnements'] });
    },
  });
}

/** Une période d'immersion vue depuis le dossier apprenant. */
export interface ImmersionDuDossier {
  id: string;
  entrepriseId: string;
  entreprise: string;
  /** Vides pour un positionnement sans immersion. */
  dateDebut: string | null;
  dateFin: string | null;
  sansImmersion: boolean;
  statut: StatutImmersionDossier;
  avisEntreprise: string | null;
  compteRendu: { id: string; dateVisite: string; modifieLe: string } | null;
}

/** Statut d'une immersion, tel que l'API le nomme. */
export type StatutImmersionDossier = 'PLANIFIEE' | 'EN_COURS' | 'VALIDEE' | 'NON_VALIDEE';

/** Motif qui empêche d'établir le contrat, calculé par l'API. */
export type RefusContrat =
  | 'MODE_OF'
  | 'IMMERSION_NON_VALIDEE'
  | 'CONTRAT_DEJA_OUVERT'
  | 'FORMATION_ABSENTE'
  | 'DATES_MANQUANTES'
  | 'DATES_INCOHERENTES';

/** Une ligne du suivi « positionnement en entreprise & immersion » (CDC §4.2). */
export interface LignePositionnement {
  /** Identifiant de l'immersion : c'est elle que l'on tranche, pas le dossier. */
  id: string;
  apprenantId: string;
  candidat: string;
  entreprise: string;
  siteCode: string;
  mode: ModeDossier;
  dateDebut: string | null;
  dateFin: string | null;
  sansImmersion: boolean;
  statut: StatutImmersionDossier;
  avisEntreprise: string | null;
  contratOuvert: boolean;
  /** `null` quand le contrat peut être établi. */
  refusContrat: RefusContrat | null;
  messageRefus: string | null;
}

/**
 * File d'attente de l'immersion au contrat, bornée au périmètre de sites.
 * Les immersions non validées n'y figurent pas : elles sont classées.
 */
export function usePositionnements(site: string) {
  const parametres = site && site !== TOUS_LES_SITES ? `?site=${encodeURIComponent(site)}` : '';
  return useQuery({
    queryKey: ['positionnements', site],
    queryFn: () =>
      appelApi<{ positionnements: LignePositionnement[] }>(
        `/apprenants/positionnements${parametres}`,
      ),
  });
}

/**
 * Établissement du contrat d'apprentissage à l'issue d'une immersion validée.
 * Le contrat naît au brouillon : la plateforme le prépare, la signature reste
 * au circuit de signature.
 */
export function useEtablirContrat() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (immersionId: string) =>
      appelApi<{ contratId: string; apprenantId: string; message: string }>(
        `/apprenants/immersions/${immersionId}/contrat`,
        { methode: 'POST' },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['positionnements'] });
      void client.invalidateQueries({ queryKey: ['apprenants'] });
      void client.invalidateQueries({ queryKey: ['dossier'] });
      void client.invalidateQueries({ queryKey: ['matching'] });
    },
  });
}

/** Renvoi du lien de création d'espace candidat. */
export function useRenvoyerLienAcces() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<{ message: string }>(`/apprenants/${id}/lien-acces`, { methode: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['apprenants'] });
      void client.invalidateQueries({ queryKey: ['dossier'] });
    },
  });
}

/** Contrôle d'une pièce : vérifiée, validée ou refusée (CDC §6.4). */
export function useControlerPiece() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      pieceId: string;
      statut: 'VERIFIEE' | 'VALIDEE' | 'REFUSEE';
      motifRefus?: string;
    }) =>
      appelApi<{ message: string }>(`/pieces/${variables.pieceId}`, {
        methode: 'PATCH',
        corps: { statut: variables.statut, motifRefus: variables.motifRefus },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['dossier'] });
      void client.invalidateQueries({ queryKey: ['apprenants'] });
    },
  });
}

/**
 * Champs modifiables du dossier depuis l'écran « Dossier apprenant ».
 *
 * Tous optionnels : la mise à jour est partielle, et l'API ne touche que ce
 * qu'elle reçoit. Le statut n'y figure pas — il découle de l'avancement, il ne
 * se saisit pas — ni le mode, qui a sa propre bascule dans l'en-tête.
 */
export interface EntreeModificationDossier {
  nom?: string;
  prenom?: string;
  dateNaissance?: string | null;
  lieuNaissance?: string | null;
  sexe?: 'M' | 'F' | null;
  nationalite?: string | null;
  ine?: string | null;
  email?: string | null;
  telephone?: string | null;
  adresse?: string | null;
  codePostal?: string | null;
  ville?: string | null;
  siteCode?: string;
  formationCode?: string | null;
  mode?: ModeDossier;
  dateEntree?: string | null;
  dateFinPrevue?: string | null;
  mobilite?: Mobilite | null;
  /** `null` détache le dossier de son conseiller référent. */
  conseillerId?: string | null;
  hebergeParTiers?: boolean;
  rqth?: boolean;
  dejaApprenti?: boolean;
  demandeurEmploi?: boolean;
  titulaireDiplome?: boolean;
  rechercheEntreprise?: boolean;
  carteEtudianteFaite?: boolean;

  // Bloc CERFA. Une chaîne vidée devient `null` : la rubrique redevient
  // « non renseignée », ce qui n'est pas la même chose qu'une chaîne vide.
  nomUsage?: string | null;
  nir?: string | null;
  departementNaissance?: string | null;
  regimeSocial?: string | null;
  sportifHautNiveau?: boolean;
  rqthEquivalenceJeunes?: boolean;
  rqthExtensionBoe?: boolean;
  projetCreationEntreprise?: boolean;
  numeroVoie?: string | null;
  voie?: string | null;
  complementAdresse?: string | null;
  situationAvantContrat?: string | null;
  dernierDiplomePrepare?: string | null;
  derniereClasseSuivie?: string | null;
  intitulePrecisDernierDiplome?: string | null;
  diplomeLePlusEleve?: string | null;
  representantNom?: string | null;
  representantPrenom?: string | null;
  representantNumeroVoie?: string | null;
  representantVoie?: string | null;
  representantComplement?: string | null;
  representantCodePostal?: string | null;
  representantCommune?: string | null;
  representantCourriel?: string | null;
}

/**
 * Mise à jour du dossier. L'API renvoie le dossier rechargé, mais les requêtes
 * sont invalidées plutôt que le cache écrasé : la modification d'une case de
 * situation change les pièces attendues, et la liste des candidats en dépend.
 */
export function useModifierDossier() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; valeurs: EntreeModificationDossier }) =>
      appelApi<{ dossier: Dossier }>(`/apprenants/${variables.id}`, {
        methode: 'PATCH',
        corps: variables.valeurs,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['dossier'] });
      void client.invalidateQueries({ queryKey: ['apprenants'] });
    },
  });
}

/**
 * Fiche de recrutement corrigée depuis le dossier apprenant.
 *
 * Elle est enregistrée sur l'entretien, pas recopiée sur le dossier : le pôle
 * commercial et le secrétariat travaillent sur le même document. La fiche part
 * entière, comme depuis le tableau des entretiens — c'est ce qui permet
 * d'effacer une rubrique.
 */
export function useEnregistrerFicheEntretien() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; fiche: FicheEntretien }) =>
      appelApi<{ dossier: Dossier }>(`/apprenants/${variables.id}/fiche-entretien`, {
        methode: 'PUT',
        corps: variables.fiche,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['dossier'] });
      void client.invalidateQueries({ queryKey: ['entretiens'] });
    },
  });
}


/**
 * Dépôt d'une pièce justificative.
 *
 * La même mutation sert au candidat depuis son espace et au secrétariat depuis
 * le dossier : l'API accepte les deux écrans et vérifie de son côté que le
 * candidat ne touche qu'à son propre dossier.
 */
export function useDeposerPiece() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { pieceId: string; fichier: File; remplace?: string }) => {
      const formulaire = new FormData();
      formulaire.append('fichier', variables.fichier);
      // Remplacement : l'API dépose le nouveau fichier puis retire l'ancien
      // dans la même opération, de sorte qu'un format refusé ne laisse pas la
      // pièce vide.
      if (variables.remplace) formulaire.append('remplace', variables.remplace);
      return appelApi<{ message: string }>(`/pieces/${variables.pieceId}/fichier`, {
        methode: 'POST',
        formulaire,
      });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['dossier'] });
      void client.invalidateQueries({ queryKey: ['apprenants'] });
      // Le candidat dépose depuis « Mes documents » (espace-apprenant), pas
      // seulement depuis le dossier : sans cette invalidation, l'écran reste
      // sur l'état chargé avant le dépôt tant qu'il n'est pas rechargé à la main.
      void client.invalidateQueries({ queryKey: ['espace-apprenant'] });
    },
  });
}

/**
 * Retrait d'un fichier déposé.
 *
 * Sans lui, une pièce qui attend deux fichiers serait un cul-de-sac : le
 * mauvais fichier déposé resterait là. L'API refuse le retrait dès que la
 * pièce est validée.
 */
export function useSupprimerFichierPiece() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { pieceId: string; fichierId: string }) =>
      appelApi<{ message: string }>(
        `/pieces/${variables.pieceId}/fichiers/${variables.fichierId}`,
        { methode: 'DELETE' },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['dossier'] });
      void client.invalidateQueries({ queryKey: ['apprenants'] });
      void client.invalidateQueries({ queryKey: ['espace-apprenant'] });
    },
  });
}
